import React, {useEffect, useRef, useState} from "react";
import styled from "styled-components";
import { useTranslation, Trans } from 'react-i18next';
import Aos from 'aos'
import 'aos/dist/aos.css'

/* Aligned to the same column as the header (shared gutters) */
const Section = styled.section`
    width: 100%;
    max-width: var(--container);
    margin: 0 auto;
    padding-left: var(--gutter);
    padding-right: var(--gutter);
`;

const Band = styled.div`
    background: var(--band);
    border-radius: var(--radius-xl);
    padding: clamp(3rem, 2.4rem + 2.5vw, 5rem) clamp(1.5rem, 1rem + 2vw, 3.5rem);
    overflow: hidden;
`;

const Head = styled.div`
    text-align: center;
    max-width: 780px;
    margin: 0 auto 3rem;
`;

const Eyebrow = styled.span`
    display: inline-flex;
    align-items: center;
    font-size: var(--fs-eyebrow);
    font-weight: 500;
    color: var(--ink-soft);
    border: 1px solid var(--line-strong);
    background: transparent;
    padding: 0.45rem 1.1rem;
    border-radius: var(--radius-pill);
    margin-bottom: 1.25rem;
`;

const Intro = styled.p`
    text-align: center;
    color: var(--ink-soft);
    font-size: var(--fs-h3);
    font-weight: 400;
    line-height: 1.6;
    letter-spacing: -0.01em;
`;

/* ---- Carousel stage: active member centered & larger ---- */
const Stage = styled.div`
    position: relative;
    height: 320px;
    max-width: 900px;
    margin: 0 auto 2.5rem;
    touch-action: pan-y;

    @media (max-width: 1024px) {
        height: 270px;
    }
`;

const slotTransforms = {
    center: 'translate(-50%, -50%) scale(1)',
    left: 'translate(-50%, -50%) translateX(-280px) scale(0.62)',
    right: 'translate(-50%, -50%) translateX(280px) scale(0.62)',
};

/* Tighter offsets on tablets so the strip never overflows the band */
const slotTransformsMid = {
    center: 'translate(-50%, -50%) scale(1)',
    left: 'translate(-50%, -50%) translateX(-190px) scale(0.55)',
    right: 'translate(-50%, -50%) translateX(190px) scale(0.55)',
};

const Slide = styled.button`
    position: absolute;
    top: 50%;
    left: 50%;
    width: 300px;
    height: 300px;
    padding: 0;
    border: none;
    border-radius: var(--radius-lg);
    overflow: hidden;
    cursor: pointer;
    background: var(--card-soft);
    transform: ${p => slotTransforms[p.$slot]};
    z-index: ${p => (p.$slot === 'center' ? 2 : 1)};
    opacity: ${p => (p.$slot === 'center' ? 1 : 0.85)};
    transition: transform 0.55s cubic-bezier(0.25, 0.8, 0.3, 1), opacity 0.4s ease;

    @media (max-width: 1024px) {
        width: 250px;
        height: 250px;
        transform: ${p => slotTransformsMid[p.$slot]};
    }
`;

const Initials = styled.span`
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--brand-700);
    font-size: 3rem;
    font-weight: 700;
    letter-spacing: -0.02em;
`;

const SlideImg = styled.img`
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    z-index: 1;
`;

/* ---- Mobile: simple stacked list (no carousel) ---- */
const MobileList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2.75rem;
`;

const MobileMember = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
`;

const MobilePhoto = styled.div`
    position: relative;
    width: 100%;
    max-width: 300px;
    aspect-ratio: 1 / 1;
    border-radius: var(--radius-lg);
    overflow: hidden;
    background: var(--card-soft);
    margin-bottom: 1.1rem;
`;

/* ---- Active member details ---- */
const ActiveInfo = styled.div`
    text-align: center;
    max-width: 620px;
    margin: 0 auto;
    animation: memberFade 0.45s ease;

    @keyframes memberFade {
        from { opacity: 0; transform: translateY(10px); }
        to   { opacity: 1; transform: translateY(0); }
    }
`;

const Name = styled.h3`
    font-size: 1.45rem;
    font-weight: 600;
    color: var(--ink);
`;

const Role = styled.span`
    display: block;
    font-size: var(--fs-body);
    font-weight: 500;
    color: var(--brand-600);
    margin: 0.35rem 0 0.9rem;
`;

const Description = styled.p`
    font-size: var(--fs-sm);
    line-height: 1.7;
    color: var(--body-color);
`;

/* Names and photos are fixed; roles and bios live in the translation files
   under team.members.<key> */
const members = [
    {
        key: "johny",
        name: "Johny Rabenantoandro",
        imageUrl: "https://media.licdn.com/dms/image/v2/D5622AQHkOJTguOe1Kw/feedshare-shrink_800/feedshare-shrink_800/0/1716541719452?e=2147483647&v=beta&t=_eMWMqTvIwnNNB0UYk82sLDKz_-fjcT-ABHAmO0rPmo"
    },
    {
        key: "lucien",
        name: "Dr. Lucien Faliniaina",
        imageUrl: "https://ehfupwzhxybqozoosqlu.supabase.co/storage/v1/object/public/images/2024/November/Faliniaina.jpg"
    },
    {
        key: "nelson",
        name: "Nelson Rabenandrianina",
        imageUrl: "https://media.licdn.com/dms/image/v2/C4D03AQGIh1P_rjYKVA/profile-displayphoto-shrink_800_800/profile-displayphoto-shrink_800_800/0/1587228204355?e=1784764800&v=beta&t=3ezfh3YS1qxLMIpt-d4LSIIwJ5Tmr1meAvnO3F_5rDE"
    },
];

const getInitials = (name = '') =>
    name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase();

const CoreTeam = () => {
    const { t } = useTranslation();
    const [activeIndex, setActiveIndex] = useState(0); // Johny in the middle by default
    const [isMobile, setIsMobile] = useState(
        () => typeof window !== 'undefined' && window.matchMedia('(max-width: 700px)').matches
    );
    const touchStartX = useRef(null);
    const wheelLock = useRef(0);

    useEffect(() => {
        Aos.init({
            duration: 2000,
            once: true
        });
    }, []);

    // Carousel on desktop, plain list on mobile
    useEffect(() => {
        const mq = window.matchMedia('(max-width: 700px)');
        const onChange = (e) => setIsMobile(e.matches);
        if (mq.addEventListener) {
            mq.addEventListener('change', onChange);
            return () => mq.removeEventListener('change', onChange);
        }
        mq.addListener(onChange);
        return () => mq.removeListener(onChange);
    }, []);

    const activeMember = members[activeIndex];

    // Active member sits in the center; the others flank it, keeping array order
    const others = members.map((_, i) => i).filter((i) => i !== activeIndex);
    const slotFor = (i) =>
        i === activeIndex ? 'center' : i === others[0] ? 'left' : 'right';

    const next = () => setActiveIndex((i) => (i + 1) % members.length);
    const prev = () => setActiveIndex((i) => (i - 1 + members.length) % members.length);

    const handleTouchStart = (e) => {
        touchStartX.current = e.touches[0].clientX;
    };

    const handleTouchMove = (e) => {
        if (touchStartX.current === null) return;
        const diffX = touchStartX.current - e.touches[0].clientX;
        if (Math.abs(diffX) > 50) {
            diffX > 0 ? next() : prev();
            touchStartX.current = null;
        }
    };

    // Horizontal trackpad scroll switches members (vertical scroll untouched)
    const handleWheel = (e) => {
        if (Math.abs(e.deltaX) <= Math.abs(e.deltaY) || Math.abs(e.deltaX) < 24) return;
        const now = Date.now();
        if (now - wheelLock.current < 500) return;
        wheelLock.current = now;
        e.deltaX > 0 ? next() : prev();
    };

    return (
        <Section>
            <Band>
                <Head>
                    <Eyebrow>{t('team.eyebrow')}</Eyebrow>
                    <Intro>
                        <Trans
                            i18nKey="team.intro"
                            components={{ hl: <span style={{ color: "var(--highlight)", fontWeight: 500 }}/> }}
                        />
                    </Intro>
                </Head>

                {isMobile ? (
                    /* Mobile: stacked list, no carousel */
                    <MobileList>
                        {members.map((member) => (
                            <MobileMember
                                key={member.name}
                                data-aos="fade-up"
                                data-aos-duration="2000"
                                data-aos-once="true"
                            >
                                <MobilePhoto>
                                    <Initials>{getInitials(member.name)}</Initials>
                                    {member.imageUrl && (
                                        <SlideImg
                                            src={member.imageUrl}
                                            alt={member.name}
                                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                        />
                                    )}
                                </MobilePhoto>
                                <Name>{member.name}</Name>
                                <Role>{t(`team.members.${member.key}.role`)}</Role>
                                <Description>{t(`team.members.${member.key}.description`)}</Description>
                            </MobileMember>
                        ))}
                    </MobileList>
                ) : (
                    <>
                        <Stage
                            data-aos="fade-up"
                            data-aos-duration="2000"
                            data-aos-once="true"
                            onTouchStart={handleTouchStart}
                            onTouchMove={handleTouchMove}
                            onWheel={handleWheel}
                        >
                            {members.map((member, i) => (
                                <Slide
                                    key={member.name}
                                    $slot={slotFor(i)}
                                    onClick={() => setActiveIndex(i)}
                                    aria-label={`${member.name} — ${t(`team.members.${member.key}.role`)}`}
                                    aria-pressed={i === activeIndex}
                                >
                                    <Initials>{getInitials(member.name)}</Initials>
                                    {member.imageUrl && (
                                        <SlideImg
                                            src={member.imageUrl}
                                            alt={member.name}
                                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                        />
                                    )}
                                </Slide>
                            ))}
                        </Stage>

                        <ActiveInfo key={activeIndex}>
                            <Name>{activeMember.name}</Name>
                            <Role>{t(`team.members.${activeMember.key}.role`)}</Role>
                            <Description>{t(`team.members.${activeMember.key}.description`)}</Description>
                        </ActiveInfo>
                    </>
                )}
            </Band>
        </Section>
    );
};

export default CoreTeam;
